import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { post } from "@utils/axios_api";
import { toast } from 'react-toastify';

const AccountSaveModal = ({
  show,
  mode = "create", // create | edit
  account,
  onClose,
  onSaved,
  compCode,
  compName,
  roleOptions,
  useYnOptions,
}) => {
  const defaultRoleId = useMemo(() => roleOptions?.[0]?.key || "R2", [roleOptions]);
  const defaultUseYn = useMemo(() => useYnOptions?.[0]?.key || "Y", [useYnOptions]);

  const isCreate = mode === "create";
  const effectiveCompCode = (isCreate ? compCode : account?.compCode ?? compCode) || "";
  const effectiveCompName = (isCreate ? compName : account?.compName ?? compName) || "";

  const [form, setForm] = useState({
    id: "",
    password: "",
    name: "",
    phoneNo: "",
    email: "",
    roleId: defaultRoleId,
    useYn: defaultUseYn,
  });
  const [duplChecked, setDuplChecked] = useState(false);
  const [isDupl, setIsDupl] = useState(false);
  const [duplMsg, setDuplMsg] = useState("");
  const [emailDuplChecked, setEmailDuplChecked] = useState(false);
  const [isEmailDupl, setIsEmailDupl] = useState(false);
  const [emailDuplMsg, setEmailDuplMsg] = useState("");

  useEffect(() => {
    if (!show) return;
    if (!isCreate && account) {
      setForm({
        id: account.id || "",
        password: "",
        name: account.name || "",
        phoneNo: account.phoneNo || "",
        email: account.email || "",
        roleId: account.roleId || defaultRoleId,
        useYn: account.useYn || defaultUseYn,
      });
    } else {
      setForm({
        id: "",
        password: "",
        name: "",
        phoneNo: "",
        email: "",
        roleId: defaultRoleId,
        useYn: defaultUseYn,
      });
    }
    setDuplChecked(false);
    setIsDupl(false);
    setDuplMsg("");
    setEmailDuplChecked(false);
    setIsEmailDupl(false);
    setEmailDuplMsg("");
  }, [show, isCreate, account, defaultRoleId, defaultUseYn]);

  const setField = (name, value) => {
    const sanitized = name === "phoneNo" ? value.replace(/\D/g, "") : value;
    setForm((prev) => ({ ...prev, [name]: sanitized }));
    if (name === "id") {
      setDuplChecked(false);
      setIsDupl(false);
      setDuplMsg("");
    }
    if (name === "email") {
      setEmailDuplChecked(false);
      setIsEmailDupl(false);
      setEmailDuplMsg("");
    }
  };

  const handleDupl = async () => {
    if (!isCreate) return;
    if (!form.id) {
      toast.warn("아이디를 입력해 주세요.");
      setDuplMsg("");
      return;
    }

    try {
      const response = await post(
        "/admin/comm/account/isdupl",
        { id: form.id, compCode: effectiveCompCode },
        { headers: { "Content-type": "application/json" } }
      );

      setDuplChecked(true);
      if (response?.isDupl) {
        setIsDupl(true);
        setDuplMsg("이미 존재하는 아이디입니다.");
      } else {
        setIsDupl(false);
        setDuplMsg("사용 가능한 아이디입니다.");
      }
    } catch (error) {
      console.error("중복확인 오류:", error);
    }
  };

  const handleEmailDupl = async () => {
    if (!isCreate) return;
    if (!form.email) {
      toast.warn("이메일을 입력해 주세요.");
      setEmailDuplMsg("");
      return;
    }

    try {
      const response = await post(
        "/admin/comm/account/isdupl-email",
        { email: form.email },
        { headers: { "Content-type": "application/json" } }
      );

      setEmailDuplChecked(true);
      if (response?.isDupl) {
        setIsEmailDupl(true);
        setEmailDuplMsg("이미 사용 중인 이메일입니다.");
      } else {
        setIsEmailDupl(false);
        setEmailDuplMsg("사용 가능한 이메일입니다.");
      }
    } catch (error) {
      console.error("이메일 중복확인 오류:", error);
    }
  };

  const handlePasswordReset = async () => {
    const digits = (form.phoneNo || "").replace(/\D/g, "");
    const newPassword = digits.length >= 4 ? digits.slice(-4) : "0000";

    if (!window.confirm(`비밀번호를 "${newPassword}"(으)로 초기화 하시겠습니까?`)) return;

    try {
      const response = await post(
        `/admin/comm/account/${form.id}/reset-password`,
        { password: newPassword },
        { headers: { "Content-type": "application/json" } }
      );
      toast.success(response?.message || "비밀번호가 초기화되었습니다.");
      onClose?.();
      onSaved?.();
    } catch (error) {
      console.error("비밀번호 초기화 오류:", error);
    }
  };

  const handleSave = async () => {
    if (!effectiveCompCode) {
      toast.warn("회사를 먼저 선택해 주세요.");
      return;
    }
    if (!form.id) {
      toast.warn("아이디를 입력해 주세요.");
      return;
    }
    if (isCreate && !form.password) {
      toast.warn("비밀번호를 입력해 주세요.");
      return;
    }
    if (isCreate && !duplChecked) {
      toast.warn("아이디 중복확인을 해주세요.");
      return;
    }
    if (isCreate && isDupl) {
      toast.warn("아이디가 중복되었습니다.");
      return;
    }
    if (isCreate && form.email && !emailDuplChecked) {
      toast.warn("이메일 중복확인을 해주세요.");
      return;
    }
    if (isCreate && isEmailDupl) {
      toast.warn("이메일이 중복되었습니다.");
      return;
    }
    if (!window.confirm(isCreate ? "등록하시겠습니까?" : "저장하시겠습니까?")) return;

    try {
      const payload = {
        compCode: effectiveCompCode,
        compName: effectiveCompName,
        ...form,
      };

      const url = isCreate ? "/admin/comm/account/register" : `/admin/comm/account/${form.id}`;
      const response = await post(url, payload, {
        headers: { "Content-type": "application/json" },
        transformRequest: [(data) => JSON.stringify(data)],
      });

      toast.success(response?.message || (isCreate ? "등록되었습니다." : "저장되었습니다."));
      onClose?.();
      onSaved?.();
    } catch (error) {
      console.error(isCreate ? "등록 오류:" : "저장 오류:", error);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header>
        <Modal.Title className="text-lg font-extrabold">{isCreate ? "계정 등록" : "계정 상세"}</Modal.Title>
        <button type="button" className="modal-btn-close" onClick={onClose}>
          <span className="modal-btn-span">X</span>
        </button>
      </Modal.Header>
      <Modal.Body>
        {/* Desktop: 기존 table-write 느낌 */}
        <div className="hidden md:block">
          <table className="table-write" style={{ width: "100%" }}>
            <colgroup>
              <col style={{ width: "20%" }} />
              <col style={{ width: "30%" }} />
              <col style={{ width: "20%" }} />
              <col style={{ width: "30%" }} />
            </colgroup>
            <tbody>
              <tr>
                <th>
                  아이디<span style={{ color: "red" }}>*</span>
                </th>
                <td colSpan="3">
                  <div className="flex flex-col xl:flex-row gap-2">
                    <input
                      value={form.id}
                      onChange={(e) => setField("id", e.target.value)}
                      disabled={!isCreate}
                      style={{ width: "100%" }}
                    />
                    {isCreate && (
                      <button
                        type="button"
                        className="btn-save whitespace-nowrap !bg-[#3533b6] hover:!bg-[#2f2da0]"
                        style={{ whiteSpace: "nowrap" }}
                        onClick={handleDupl}
                      >
                        중복확인
                      </button>
                    )}
                  </div>
                  {isCreate && <label style={{ display: duplMsg ? "" : "none" }}>{duplMsg}</label>}
                </td>
              </tr>
              {isCreate && (
                <tr>
                  <th>
                    비밀번호<span style={{ color: "red" }}>*</span>
                  </th>
                  <td colSpan="3">
                    <input
                      type="password"
                      value={form.password}
                      onChange={(e) => setField("password", e.target.value)}
                      placeholder="비밀번호 입력"
                      style={{ width: "100%" }}
                    />
                  </td>
                </tr>
              )}
              <tr>
                <th>
                  회사<span style={{ color: "red" }}>*</span>
                </th>
                <td colSpan="3">
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-2">
                    <input value={effectiveCompCode} readOnly style={{ width: "100%" }} />
                    <input value={effectiveCompName} readOnly style={{ width: "100%" }} />
                  </div>
                </td>
              </tr>
              <tr>
                <th>이름</th>
                <td colSpan="3">
                  <input value={form.name} onChange={(e) => setField("name", e.target.value)} style={{ width: "100%" }} />
                </td>
              </tr>
              <tr>
                <th>전화번호</th>
                <td colSpan="3">
                  <input
                    value={form.phoneNo}
                    onChange={(e) => setField("phoneNo", e.target.value)}
                    placeholder="- 없이 숫자만 입력해 주세요."
                    maxLength={11}
                    style={{ width: "100%" }}
                  />
                  <p style={{ fontSize: '11px', color: '#888', margin: '3px 0 0' }}>- 없이 숫자만 입력하세요.</p>
                </td>
              </tr>
              <tr>
                <th>이메일</th>
                <td colSpan="3">
                  <div className="flex flex-col xl:flex-row gap-2">
                    <input
                      value={form.email}
                      onChange={(e) => setField("email", e.target.value)}
                      style={{ width: "100%" }}
                    />
                    {isCreate && (
                      <button
                        type="button"
                        className="btn-save whitespace-nowrap !bg-[#3533b6] hover:!bg-[#2f2da0]"
                        style={{ whiteSpace: "nowrap" }}
                        onClick={handleEmailDupl}
                      >
                        중복확인
                      </button>
                    )}
                  </div>
                  {isCreate && <label style={{ display: emailDuplMsg ? "" : "none", color: isEmailDupl ? "#ef4444" : "#16a34a" }}>{emailDuplMsg}</label>}
                </td>
              </tr>
              <tr>
                <th>권한</th>
                <td>
                  <select value={form.roleId} onChange={(e) => setField("roleId", e.target.value)} style={{ width: "100%" }}>
                    {(roleOptions || []).map((opt) => (
                      <option key={opt.key} value={opt.key}>
                        {opt.name}
                      </option>
                    ))}
                  </select>
                </td>
                <th>사용여부</th>
                <td>
                  <select value={form.useYn} onChange={(e) => setField("useYn", e.target.value)} style={{ width: "100%" }}>
                    {(useYnOptions || []).map((opt) => (
                      <option key={opt.key} value={opt.key}>
                        {opt.name}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Mobile: 카드 폼 */}
        <div className="md:hidden rounded-xl border border-gray-200 bg-white p-4 shadow-sm space-y-4">
          <div>
            <label className="block text-[14px] font-bold text-gray-700 mb-1">
              아이디 <span className="text-red-500">*</span>
            </label>
            <input
              value={form.id}
              onChange={(e) => setField("id", e.target.value)}
              disabled={!isCreate}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[16px]"
            />
            {isCreate && (
              <button
                type="button"
                className="btn-save w-full mt-2 whitespace-nowrap !bg-[#3533b6] hover:!bg-[#2f2da0]"
                style={{ whiteSpace: "nowrap" }}
                onClick={handleDupl}
              >
                중복확인
              </button>
            )}
            {isCreate && !!duplMsg && (
              <p className="mt-1 text-[13px]" style={{ color: isDupl ? "#ef4444" : "#16a34a" }}>
                {duplMsg}
              </p>
            )}
          </div>

          {isCreate && (
            <div>
              <label className="block text-[14px] font-bold text-gray-700 mb-1">
                비밀번호 <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => setField("password", e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[16px]"
                placeholder="비밀번호 입력"
              />
            </div>
          )}

          <div>
            <label className="block text-[14px] font-bold text-gray-700 mb-1">
              회사 <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input value={effectiveCompCode} readOnly className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-[16px]" />
              <input value={effectiveCompName} readOnly className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-[16px]" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[14px] font-bold text-gray-700 mb-1">이름</label>
              <input
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[16px]"
              />
            </div>
            <div>
              <label className="block text-[14px] font-bold text-gray-700 mb-1">전화번호</label>
              <input
                value={form.phoneNo}
                onChange={(e) => setField("phoneNo", e.target.value)}
                placeholder="- 없이 숫자만 입력해 주세요."
                maxLength={11}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[16px]"
              />
              <p style={{ fontSize: '11px', color: '#888', margin: '3px 0 0' }}>- 없이 숫자만 입력하세요.</p>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-[14px] font-bold text-gray-700 mb-1">이메일</label>
              <input
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[16px]"
              />
              {isCreate && (
                <button
                  type="button"
                  className="btn-save w-full mt-2 whitespace-nowrap !bg-[#3533b6] hover:!bg-[#2f2da0]"
                  onClick={handleEmailDupl}
                >
                  중복확인
                </button>
              )}
              {isCreate && !!emailDuplMsg && (
                <p className="mt-1 text-[13px]" style={{ color: isEmailDupl ? "#ef4444" : "#16a34a" }}>
                  {emailDuplMsg}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[14px] font-bold text-gray-700 mb-1">권한</label>
              <select
                value={form.roleId}
                onChange={(e) => setField("roleId", e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-[16px]"
              >
                {(roleOptions || []).map((opt) => (
                  <option key={opt.key} value={opt.key}>
                    {opt.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[14px] font-bold text-gray-700 mb-1">사용여부</label>
              <select
                value={form.useYn}
                onChange={(e) => setField("useYn", e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-[16px]"
              >
                {(useYnOptions || []).map((opt) => (
                  <option key={opt.key} value={opt.key}>
                    {opt.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn-cancel" onClick={onClose}>
          취소
        </button>
        {!isCreate && (
          <button type="button" className="btn-save" onClick={handlePasswordReset}>
            비밀번호 초기화
          </button>
        )}
        <button
          type="button"
          className="btn-save !bg-[#3533b6] hover:!bg-[#2f2da0]"
          onClick={handleSave}
        >
          {isCreate ? "등록" : "저장"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default AccountSaveModal;
